import { cva, type VariantProps } from 'class-variance-authority'
import { ChangeEvent, forwardRef, InputHTMLAttributes, ReactNode, useMemo, useState } from 'react'

import { cn } from '@/lib/utils'

const textFieldVariants = cva('relative w-full', {
  variants: {
    variant: {
      default: '',
      error: '',
      focused: '',
      disabled: '',
      hovered: '',
      success: '',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const inputContainerVariants = cva('relative w-full rounded-2xl border transition-all duration-200 bg-white', {
  variants: {
    variant: {
      default: 'border-border-default',
      hovered: 'border-[#B5BAC2]',
      focused: 'border-icon-primary shadow-[0_0_0_2px_rgba(0,174,255,0.2)]',
      error: 'border-destructive shadow-[0_0_0_2px_rgba(255,0,0,0.2)]',
      success: 'border-[#008000]',
      disabled: 'border-text-disabled bg-background-surface',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const inputVariants = cva(
  'w-full bg-transparent border-0 outline-none transition-all duration-200 text-[#333333] [&:-webkit-autofill]:rounded-2xl [&:-webkit-autofill]:shadow-[inset_0_0_0_1000px_rgb(255,255,255)]',
  {
    variants: {
      variant: {
        default: '',
        hovered: '',
        focused: '',
        error: '',
        disabled: 'text-[#9E9E9E] cursor-not-allowed',
        success: '',
      },
      hasLeadingIcon: {
        true: 'ps-12',
        false: 'ps-4',
      },
      hasTrailingIcon: {
        true: 'pe-12',
        false: 'pe-4',
      },
    },
    defaultVariants: {
      variant: 'default',
      hasLeadingIcon: false,
      hasTrailingIcon: false,
    },
  }
)

const floatingLabelVariants = cva('absolute right-4 transition-all duration-200 pointer-events-none px-1 z-10', {
  variants: {
    variant: {
      default: 'text-[#4B4B4B]',
      error: 'text-[#FF0000]',
      success: 'text-[#008000]',
      disabled: 'text-[#9E9E9E]',
    },
    hasLeadingIcon: {
      true: '!start-8',
    },
    isFloating: {
      true: 'top-0 -translate-y-1/2 text-xs bg-white !start-0',
      false: 'top-1/2 -translate-y-1/2 text-sm text-[#6B7280]',
    },
  },
  defaultVariants: {
    variant: 'default',
    isFloating: false,
  },
})

const supportingTextVariants = cva('text-xs mt-1 px-4', {
  variants: {
    variant: {
      default: 'text-[#4B4B4B]',
      error: 'text-[#FF0000]',
      success: 'text-[#008000]',
      disabled: 'text-[#9E9E9E]',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>, VariantProps<typeof textFieldVariants> {
  label?: string
  supportingText?: string
  error?: boolean
  disabled?: boolean
  icon?: ReactNode
  success?: boolean
  trailingIcon?: ReactNode
}

const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  ({ className, label, supportingText, error = false, disabled = false, icon, trailingIcon, value, success = false, ...props }, ref) => {
    const [isFocused, setIsFocused] = useState(false)
    const [isHovered, setIsHovered] = useState(false)
    const [internalValue, setInternalValue] = useState('')

    const currentValue = value !== undefined ? value : internalValue
    const hasValue = currentValue !== undefined && currentValue !== ''
    const isLabelFloating = isFocused || hasValue

    const currentVariant = useMemo(() => {
      if (error) return 'error'
      if (disabled) return 'disabled'
      if (isFocused) return 'focused'
      if (isHovered) return 'hovered'
      if (success) return 'success'
      return 'default'
    }, [error, disabled, isFocused, isHovered, success])

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      if (value === undefined) {
        setInternalValue(e.target.value)
      }
      props.onChange?.(e)
    }

    return (
      <div className={cn(textFieldVariants({ variant: currentVariant, className }))}>
        <div
          className={cn(inputContainerVariants({ variant: currentVariant }))}
          onMouseEnter={() => !disabled && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {label && (
            <label
              className={cn(
                floatingLabelVariants({
                  variant: error ? 'error' : disabled ? 'disabled' : success ? 'success' : 'default',
                  isFloating: isLabelFloating,
                  hasLeadingIcon: !!icon,
                })
              )}
              htmlFor={props.id}
            >
              {label}
            </label>
          )}

          <div className='relative flex h-12 items-center'>
            {icon && (
              <div className='absolute start-3 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center text-gray-400'>{icon}</div>
            )}

            <input
              ref={ref}
              className={cn(
                inputVariants({
                  variant: currentVariant,
                  hasLeadingIcon: !!icon,
                  hasTrailingIcon: !!trailingIcon,
                }),
                'h-full py-3 text-sm'
              )}
              disabled={disabled}
              value={currentValue}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              onBlurCapture={() => setIsFocused(false)}
              onChange={handleChange}
              {...props}
            />

            {trailingIcon && (
              <div className='absolute top-1/2 left-3 flex h-6 w-6 -translate-y-1/2 items-center justify-center text-gray-400'>
                {trailingIcon}
              </div>
            )}
          </div>
        </div>

        {supportingText && (
          <p
            className={cn(
              supportingTextVariants({
                variant: error ? 'error' : disabled ? 'disabled' : success ? 'success' : 'default',
              })
            )}
          >
            {supportingText}
          </p>
        )}
      </div>
    )
  }
)

TextField.displayName = 'TextField'

export { TextField, textFieldVariants }
